// AddAppointment.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Classes from "./AddAppointment.module.css";

import Step1Appointment from "./Comp/Step1Appointment";
import Step2Vehicle from "./Comp/Step2Vehicle";
import Step3Location from "./Comp/Step3Location";
import ConfirmBookingModal from "./Comp/ConfirmBookingModal";

import { CalenderSimpleIcon, CarFrontIcon, Location2Icon } from "../../../../../../utils/Icons";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import backend from "../../../../../../api";

/* ───────────────────────────────────── Domain dropdowns ───────────────────────────────────── */
export const FUEL_TYPES = [
  { value: "reg", label: "Regular (87 Octane)" },
  { value: "mid", label: "Diesel " },
  { value: "prem", label: "Premium (91-93 Octane)" },
];

export const FUEL_LEVELS = [
  { value: "empty", label: "Empty" },
  { value: "quarter", label: "Quarter Tank" },
  { value: "half", label: "Half Tank" },
  { value: "threeQuarter", label: "Three-Quarter Tank" },
  { value: "full", label: "Full" },
];

export const REQUESTED_AMOUNTS = [
  { value: "5", label: "5 Gallons" },
  { value: "10", label: "10 Gallons" },
  { value: "15", label: "15 Gallons" },
  { value: "20", label: "20 Gallons" },
  { value: "25", label: "25 Gallons" },
  { value: "30", label: "30 Gallons" },
];

/* ───────────────────────────────────── Initial state ───────────────────────────────────── */
const EMPTY_APPOINTMENT = {
  appointment: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    notes: "",
    date: "",
    timeSlot: "", // "09:00 — 10:00"
  },
  vehicle: {
    selectedVehicleId: "",
    make: "",
    model: "",
    license: "",
    fuelTypeId: "",
    fuelLevelId: "",
    requestedAmount: "",
  },
  extraVehicles: [],
  location: {
    selectedAddressId: "",
    address1: "",
    city: "",
    country: "",
    region: "",
    postalCode: "",
    neighborhood: "",
    gateCode: "",
    parkingSpotOrNotes: "",
    additionalNotes: "",
    confirmGasCap: false,
  },
};

const STORAGE_KEY = "fuelApptDraft";
const slideVariants = {
  initial: { x: 10, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: -10, opacity: 0 },
};

const PRICE_KEY = { reg: "regular", mid: "mid", prem: "premium" };
const TYPE_PARAM_TO_FUEL = { regular: "reg", Diesel: "mid", premium: "prem" };

export default function AddAppointment() {
  const scrollerRef = useRef(null);
  const firstStepRender = useRef(true);
  const [activeStep, setActiveStep] = useState(1);
  const [draft, setDraft] = useState(EMPTY_APPOINTMENT);
  const [service, setService] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [rush, setRush] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const { user, token } = useSelector((s) => s?.auth);
  const userId = user?._id;
  const company = useSelector((s) => s?.companyInfo?.info);
  const companyId = company?._id || "68120023c24987567a7249fe";

  const [searchParams] = useSearchParams();
  const types = searchParams.getAll("type");
  const rushFromQuery = searchParams.get("rush") === "1";

  // load draft from localStorage
  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed.activeStep) setActiveStep(parsed.activeStep);
      if (parsed.draft) setDraft(parsed.draft);
    } catch {
      /* ignore */
    }
  }, []);

  // persist draft
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ activeStep, draft }));
  }, [activeStep, draft]);

  // preselect fuel type + rush from query
  useEffect(() => {
    if (!draft.vehicle.fuelTypeId && types?.length) {
      const mapped = TYPE_PARAM_TO_FUEL[types[0]] || "";
      if (mapped) {
        setDraft((p) => ({
          ...p,
          vehicle: { ...p.vehicle, fuelTypeId: mapped },
        }));
      }
    }
    setRush(rushFromQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types, rushFromQuery]);

  // fetch service by companyId
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const { data } = await backend.get(`/api/services/by-company/${companyId}`);
        if (data?.ok) setService(data.data || null);
      } catch (e) {
        console.error("Fetch service error:", e);
      }
    })();
  }, [companyId]);

  // prefill from logged-in user
  const didPrefill = useRef(false);
  useEffect(() => {
    if (!user || didPrefill.current) return;

    const nameFirst = (user.firstName || (user.name?.split(" ")[0] ?? "")).trim();
    const nameLast = (user.lastName || (user.name?.split(" ").slice(1).join(" ") ?? "")).trim();
    const email = (user.email || "").trim();
    const phone = (user.phone || "").trim();

    const loc = user?.customerProfile?.location || {};
    const companyName = (loc.company || "").trim();
    const notes = (loc.notes || "").trim();

    const addr = {
      address1: loc.address || "",
      city: loc.city || "",
      region: loc.state || "",
      postalCode: loc.postalCode || "",
      neighborhood: loc.neighborhood || "",
      gateCode: loc.gateCode || "",
      parkingSpotOrNotes: loc.parkingSpot || "",
    };

    setDraft((prev) => {
      const a = prev.appointment || {};
      const l = prev.location || {};

      const nextAppointment = {
        ...a,
        firstName: a.firstName || nameFirst,
        lastName: a.lastName || nameLast,
        email: a.email || email,
        phone: a.phone || phone,
        company: a.company || companyName,
        notes: a.notes || notes,
      };

      const nextLocation = {
        ...l,
        address1: l.address1 || addr.address1,
        city: l.city || addr.city,
        region: l.region || addr.region,
        postalCode: l.postalCode || addr.postalCode,
        neighborhood: l.neighborhood || addr.neighborhood,
        gateCode: l.gateCode || addr.gateCode,
        parkingSpotOrNotes: l.parkingSpotOrNotes || addr.parkingSpotOrNotes,
      };

      const changed =
        JSON.stringify(a) !== JSON.stringify(nextAppointment) ||
        JSON.stringify(l) !== JSON.stringify(nextLocation);

      if (!changed) return prev;
      return { ...prev, appointment: nextAppointment, location: nextLocation };
    });

    didPrefill.current = true;
  }, [user]);

  const go = (n) => setActiveStep(n);
  const next = () => setActiveStep((p) => Math.min(p + 1, 3));
  const back = () => setActiveStep((p) => Math.max(p - 1, 1));

  const setAppointment = useCallback((partial) => {
    setDraft((prev) => ({
      ...prev,
      appointment: { ...prev.appointment, ...partial },
    }));
  }, []);

  const setVehicle = useCallback((partial) => {
    setDraft((prev) => ({
      ...prev,
      vehicle: { ...prev.vehicle, ...partial },
    }));
  }, []);

  const setLocation = useCallback((partial) => {
    setDraft((prev) => ({
      ...prev,
      location: { ...prev.location, ...partial },
    }));
  }, []);

  const addExtraVehicle = () => {
    setDraft((p) => {
      if (p.extraVehicles.length >= 3) return p;
      return {
        ...p,
        extraVehicles: [
          ...p.extraVehicles,
          {
            make: "",
            model: "",
            license: "",
            fuelTypeId: "",
            fuelLevelId: "",
            requestedAmount: "",
          },
        ],
      };
    });
  };

  const setExtraVehicle = (idx, partial) => {
    setDraft((p) => {
      const copy = [...p.extraVehicles];
      copy[idx] = { ...copy[idx], ...partial };
      return { ...p, extraVehicles: copy };
    });
  };

  const removeExtraVehicle = (idx) => {
    setDraft((p) => {
      const copy = [...p.extraVehicles];
      copy.splice(idx, 1);
      return { ...p, extraVehicles: copy };
    });
  };

  const savedVehicles = useMemo(() => {
    const arr =
      user?.customerProfile?.vehicle?.vehicles ??
      user?.customerProfile?.vehicle.vehicles ??
      [];
    return (arr || []).map((v, i) => ({
      value: String(i),
      label: `${v.make || ""} ${v.model || ""}${
        v.plate ? " — " + v.plate : ""
      }`.trim(),
      make: v.make || "",
      model: v.model || "",
      license: v.plate || v.license || "",
    }));
  }, [user]);

  const savedAddresses = useMemo(() => {
    const loc = user?.customerProfile?.location;
    if (!loc || !loc.address) return [];
    return [
      {
        value: "addr1",
        label: `${loc.address}${
          loc.city ? ", " + loc.city : ""
        }${loc.state ? ", " + loc.state : ""}${
          loc.postalCode ? " " + loc.postalCode : ""
        }`,
        address1: loc.address || "",
        city: loc.city || "",
        region: loc.state || "",
        postalCode: loc.postalCode || "",
        neighborhood: loc.neighborhood || "",
        gateCode: loc.gateCode || "",
        parkingSpotOrNotes: loc.parkingSpot || "",
      },
    ];
  }, [user]);

  const parseGallons = (val) => {
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : 0;
  };

  const priceFor = (fuelTypeId) => {
    const key = PRICE_KEY[fuelTypeId] || "regular";
    return Number(service?.prices?.[key] || 0);
  };

  const computeTotals = useCallback(
    (rushOverride) => {
      const enableRush =
        typeof rushOverride === "boolean" ? rushOverride : rush;

      let subtotal =
        parseGallons(draft.vehicle.requestedAmount) *
        priceFor(draft.vehicle.fuelTypeId);

      for (const ev of draft.extraVehicles) {
        subtotal +=
          parseGallons(ev.requestedAmount) * priceFor(ev.fuelTypeId);
      }

      const deliveryFee = Number(
        service?.fees?.delivery ?? service?.prices?.deliveryFee ?? 9.99
      );
      const baseEmergency = Number(
        service?.fees?.emergency ?? service?.prices?.emergencyFee ?? 0
      );
      const emergencyFee = enableRush ? baseEmergency : 0;
      const total = Number(subtotal + deliveryFee + emergencyFee);

      return {
        subtotal: Number(subtotal.toFixed(2)),
        deliveryFee: Number(deliveryFee.toFixed(2)),
        emergencyFee: Number(emergencyFee.toFixed(2)),
        total: Number(total.toFixed(2)),
        perGallon: {
          regular: Number(service?.prices?.regular || 0),
          mid: Number(service?.prices?.mid || 0),
          premium: Number(service?.prices?.premium || 0),
        },
        baseEmergency,
      };
    },
    [draft.vehicle, draft.extraVehicles, service, rush]
  );

  const openConfirm = () => {
    if (!draft.appointment?.date || !draft.appointment?.timeSlot) {
      alert("Please select a date and one-hour time slot first.");
      return;
    }
    if (
      !draft.location?.address1 ||
      !draft.location?.city ||
      !draft.location?.region ||
      !draft.location?.postalCode
    ) {
      alert("Please complete the address fields.");
      return;
    }
    setShowConfirm(true);
  };

  const handlePayment = async (currentRush /* boolean */) => {
    const a = draft.appointment;
    const l = draft.location;

    if (!a.date || !a.timeSlot)
      return alert("Please select date & time.");
    if (!l.address1 || !l.city || !l.region || !l.postalCode)
      return alert("Please complete the address.");

    const vehicles = [
      {
        make: draft.vehicle.make,
        model: draft.vehicle.model,
        license: draft.vehicle.license,
        fuelTypeId: draft.vehicle.fuelTypeId,
        requestedAmount: draft.vehicle.requestedAmount || "0",
      },
      ...draft.extraVehicles.map((v) => ({
        make: v.make,
        model: v.model,
        license: v.license,
        fuelTypeId: v.fuelTypeId,
        requestedAmount: v.requestedAmount || "0",
      })),
    ];

    const contact = {
      firstName: a.firstName?.trim() || "",
      lastName: a.lastName?.trim() || "",
      email: a.email?.trim() || "",
      phone: a.phone?.trim() || "",
      company: a.company?.trim() || "",
      notes: (a.notes || "").slice(0, 300),
    };

    const location = {
      address1: l.address1 || "",
      city: l.city || "",
      region: l.region || "",
      postalCode: l.postalCode || "",
      neighborhood: l.neighborhood || "",
      gateCode: l.gateCode || "",
      parkingSpotOrNotes: l.parkingSpotOrNotes || "",
      additionalNotes: l.additionalNotes || "",
    };

    try {
      setPaymentLoading(true);

      const payload = {
        companyId,
        userId,
        rush: !!currentRush,
        appointment: { date: a.date, timeSlot: a.timeSlot },
        vehicles,
        location,
        contact,
        mode: "payment",
        success_url: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/add-appointment?canceled=1`,
        metadata: { type: "booking" },
      };

      const resp = await backend.post(
        "/pay/create-checkout-session",
        payload,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );

      const url = resp?.data?.url;
      if (url) {
        localStorage.removeItem(STORAGE_KEY);
        window.location.assign(url);
      } else {
        alert("Could not start payment.");
      }
    } catch (err) {
      console.error("Error creating Checkout Session:", err);
      alert("Payment error: " + (err?.response?.data?.error || err.message));
    } finally {
      setPaymentLoading(false);
      setShowConfirm(false);
    }
  };

  const onSelectSavedVehicle = (vehicleId) => {
    const v = savedVehicles.find((x) => x.value === vehicleId);
    setDraft((p) => ({
      ...p,
      vehicle: {
        ...p.vehicle,
        selectedVehicleId: vehicleId,
        make: v?.make || "",
        model: v?.model || "",
        license: v?.license || "",
      },
    }));
  };

  const onSelectSavedAddress = (addressId) => {
    const a = savedAddresses.find((x) => x.value === addressId);
    setLocation({
      selectedAddressId: addressId,
      address1: a?.address1 || "",
      city: a?.city || "",
      region: a?.region || "",
      postalCode: a?.postalCode || "",
      neighborhood: a?.neighborhood || "",
      gateCode: a?.gateCode || "",
      parkingSpotOrNotes: a?.parkingSpotOrNotes || "",
    });
  };

  const steps = [
    {
      id: 1,
      label: "Appointment Details",
      icon: <CalenderSimpleIcon height="16px" fill="white" />,
    },
    {
      id: 2,
      label: "Vehicle & Service Info",
      icon: <CarFrontIcon height="18px" fill="white" />,
    },
    {
      id: 3,
      label: "Location Info",
      icon: <Location2Icon height="18px" fill="white" />,
    },
  ];

  useEffect(() => {
    if (firstStepRender.current) {
      firstStepRender.current = false;
      return;
    }
    const scrollNow = () => {
      const el = scrollerRef.current;
      if (
        el &&
        (el.scrollHeight > el.clientHeight ||
          getComputedStyle(el).overflowY !== "visible")
      ) {
        el.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const se =
          document.scrollingElement || document.documentElement;
        se.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(scrollNow);
      return () => cancelAnimationFrame(raf2);
    });

    return () => cancelAnimationFrame(raf1);
  }, [activeStep]);

  return (
    <div className={Classes.hero}>
      <div className={Classes.body} ref={scrollerRef}>
        <div className={Classes.bodyInner}>
          <motion.div
            className={Classes.stepper}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ type: "tween", duration: 0.35, delay: 0.1 }}
          >
            {steps.map((s, i) => (
              <React.Fragment key={s.id}>
                <div
                  role="button"
                  className={`${Classes.sideElement} ${
                    activeStep === s.id ? Classes.current : ""
                  }`}
                >
                  <div className={Classes.flexAlign10}>
                    <div
                      className={`${Classes.imageCon} ${
                        activeStep >= s.id ? Classes.active : ""
                      }`}
                    >
                      {s.icon}
                    </div>
                    <h5
                      className={
                        activeStep >= s.id ? Classes.activeText : undefined
                      }
                    >
                      {s.label}
                    </h5>
                  </div>
                </div>
                {i !== steps.length - 1 && (
                  <div
                    className={`${Classes.hr} ${
                      activeStep > s.id ? Classes.coloredHr : ""
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              variants={slideVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className={Classes.infoMain}
            >
              {activeStep === 1 && (
                <Step1Appointment
                  draft={draft}
                  setAppointment={setAppointment}
                  next={next}
                  service={service}
                  companyId={companyId} // ⬅️ NEW: needed for slot availability
                />
              )}
              {activeStep === 2 && (
                <Step2Vehicle
                  draft={draft}
                  setVehicle={setVehicle}
                  addExtraVehicle={addExtraVehicle}
                  setExtraVehicle={setExtraVehicle}
                  removeExtraVehicle={removeExtraVehicle}
                  savedVehicles={savedVehicles}
                  onSelectSavedVehicle={onSelectSavedVehicle}
                  back={back}
                  next={next}
                />
              )}
              {activeStep === 3 && (
                <Step3Location
                  draft={draft}
                  setLocation={setLocation}
                  savedAddresses={savedAddresses}
                  onSelectSavedAddress={onSelectSavedAddress}
                  back={back}
                  finish={openConfirm}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <ConfirmBookingModal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        draft={draft}
        service={service}
        rush={rush}
        setRush={setRush}
        computeTotals={computeTotals}
        onConfirm={(currentRush) => {
          if (!paymentLoading) handlePayment(currentRush);
        }}
      />
    </div>
  );
}
